/**
 * @description 页面字段无有效值时使用的统一展示占位符
 */
export const EMPTY_VALUE_PLACEHOLDER = '--'

/**
 * @description 官网保修政策页：帮助中心 /p/store/content/info 的 contentId（生产 / 非生产）
 * @reason 与 pages/website/service/help/[id].vue 同源接口，按 NUXT_PUBLIC_ENV 在 api 层选用
 */
export const WARRANTY_POLICY_HELP_CONTENT_ID_PRODUCTION = 824453152133190
export const WARRANTY_POLICY_HELP_CONTENT_ID_TEST = 7

/**
 * @description 官网退换货政策页：帮助中心 /p/store/content/info 的 contentId（生产 / 非生产）
 * @reason 与保修政策同源；请按各环境帮助中心「退换货政策」文章实际 contentId 维护
 */
export const EXCHANGE_POLICY_HELP_CONTENT_ID_PRODUCTION = 824453152133191
export const EXCHANGE_POLICY_HELP_CONTENT_ID_TEST = 8

export const FILE_TYPE = {
  IMAGE: 'image',
  VIDEO: 'video',
  WORD: 'word',
  EXCEL: 'excel',
  PPT: 'ppt',
  PDF: 'pdf',
  AUDIO: 'audio',
  ZIP: 'zip',
  TXT: 'txt',
  UNKNOWN: 'unknown',
}
// 文件后缀名常量定义
export const FILE_EXTS = {
  [FILE_TYPE.IMAGE]: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif'],
  [FILE_TYPE.VIDEO]: ['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm', 'flv', 'm4v'],
  [FILE_TYPE.WORD]: ['doc', 'docx', 'docm'],
  [FILE_TYPE.EXCEL]: ['xls', 'xlsx', 'csv', 'xlsm'],
  [FILE_TYPE.PPT]: ['ppt', 'pptx', 'pptm'],
  [FILE_TYPE.AUDIO]: ['mp3', 'wav', 'flac', 'm4a', 'ogg', 'aac'],
  [FILE_TYPE.TXT]: ['txt', 'log'],
  [FILE_TYPE.PDF]: ['pdf'],
  [FILE_TYPE.ZIP]: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'],
}

/**
 * @description 支付方式枚举
 */
export const PAYMENT_METHODS = {
  ALIPAY: {
    code: 'alipay',
    name: '支付宝支付',
    icon: '/images/pay/alipay.svg',
  },
  WECHAT: {
    code: 'wechat',
    name: '微信支付',
    icon: '/images/pay/wechat.svg',
  },
  WALLET: {
    code: 'wallet',
    name: '钱包支付',
    icon: '/images/pay/wallet.svg',
  },
  EBANK: {
    code: 'ebank',
    name: '网银B2B支付',
    icon: '/images/pay/ebank.svg',
  },
  YJZF: {
    code: 'yjzf',
    name: '银行卡支付',
    icon: '/images/pay/bank_card.svg',
  },
  INSTALLMENT: {
    code: 'installment',
    name: '信用卡支付',
    icon: '/images/pay/credit_card.svg',
  },
  CREDIT_CARD: {
    code: 'credit_card',
    name: '信用卡支付',
    icon: '/images/pay/credit_card.svg',
  },
  WEBANK: {
    code: 'webank',
    name: '微众银行',
    icon: '/images/pay/webank.svg',
  },
  NBBANK: {
    code: 'nbbank',
    name: '宁波银行',
    icon: '/images/pay/nbbank.webp',
  },
  BANK_CARD: {
    code: 'bank_card',
    name: '银行卡支付',
    icon: '/images/pay/bank_card.svg',
  },
  TRANSFER: {
    code: 'transfer',
    name: '对公转账',
    icon: '/images/pay/transfer.svg',
  },
  QUOTA: {
    code: 'quota',
    name: '平台授信金',
    icon: '/images/store/icons/ip_com.svg',
  },
}

/**
 * @description 根据支付代码获取支付方式信息
 * @param {string} code - 支付代码
 * @returns {Object} 支付方式信息
 */
export const getPaymentMethodByCode = (code) => {
  return Object.values(PAYMENT_METHODS).find((item) => item.code === code) || {}
}

/**
 * @description C 端订单支付方式回显映射：与服务端权威枚举 sales_order.pay_type 对齐
 * （TDC/apps/api/src/modules/payment/payment-pay-type.helpers.ts:SALES_ORDER_PAY_TYPE）
 * @reason mall4j 易宝旧枚举（如 12=银行卡）与 TDC 编号存在数值撞车，C 端订单详情不能复用旧 payTypeMap
 */
export const TDC_PAY_TYPE_INFO = {
  2: {
    code: 'alipay',
    name: '支付宝支付',
    icon: '/images/pay/alipay.svg',
  },
  9: {
    code: 'balance',
    name: '余额支付',
    icon: '/images/pay/wallet.svg',
  },
  11: {
    code: 'transfer',
    name: '对公转账',
    icon: '/images/pay/transfer.svg',
  },
  12: {
    code: 'quota',
    name: '平台授信金',
    icon: '/images/store/icons/ip_com.svg',
  },
}

export const ORDER_STATUS = {
  ALL: 0,
  PENDING_PAYMENT: 1,
  PENDING_SHIPMENT: 2,
  PENDING_RECEIPT: 3,
  PENDING_REVIEW: 4,
  SUCCESS: 5,
  FAILED: 6,
  PARTIAL_SHIPMENT: 7,
  /** 代理商订单「待审核」，与后端 status / 个人中心统计约定一致 */
  PENDING_AUDIT: 8,
  CLOSED: 9,
}

export const PICKUP_STATUS = {
  UNKNOWN: 0,
  NOT_PICKED: 1,
  PICKED: 2,
}

/**
 * @description 订单类型
 */
export const ORDER_TYPE = {
  NORMAL: 0, // 普通订单
  GROUP: 1, // 拼团订单
  SECKILL: 2, // 秒杀订单
  SCORE: 3, // 积分订单
}

/**
 * @description 订单模式
 */
export const ORDER_MOLD = {
  PHYSICAL: 0, // 普通实物订单
  VIRTUAL: 1, // 虚拟商品订单
  VOUCHER: 3, // 电子卡券订单
}

/**
 * @description 退款状态 (订单级 refundStatus)，与后端/订单详情约定一致：2=退款成功 4=退款失败
 */
export const REFUND_STATUS = {
  NONE: 0,
  REFUNDING: 1, // 申请退款/退款中
  SUCCESS: 2, // 退款成功
  PARTIAL_SUCCESS: 3, // 部分退款成功
  FAILED: 4, // 退款失败
  PARTIAL_REFUNDING: 5, // 部分退款中
  REJECTED: 7, // 商家拒绝
}

/**
 * @description 退款/售后业务状态 (商品级 returnMoneySts)
 */
export const RETURN_MONEY_STS = {
  NONE: 0,
  BUYER_APPLIED: 1, // 买家申请
  SELLER_ACCEPTED: 2, // 商家接受
  BUYER_SHIPPED: 3, // 买家发货
  SELLER_RECEIVED: 4, // 商家收货
  SUCCESS: 5, // 退款成功
  WITHDRAWN: 6, // 买家撤回申请
  REJECTED: 7, // 商家拒绝
  CLOSED: -1, // 退款关闭
}

/**
 * @description 退款订单列表状态 (refundStatus)
 */
export const REFUND_LIST_STATUS = {
  PROCESSING: 0, // 处理中
  PROCESSED: 1, // 已处理
}

/**
 * @description 配送方式
 */
export const DELIVERY_TYPE = {
  SELLER: 0, // 商家配送
  EXPRESS: 1, // 快递配送
  PICKUP: 2, // 到店自提
  NONE: 3, // 无需快递
  CITY_WIDE: 4, // 同城配送
}

/**
 * @description 评价状态
 */
export const COMMENT_STATUS = {
  UNCOMMENTED: 0, // 未评价
}

export const ORDER_STATUS_I18N = {
  [ORDER_STATUS.ALL]: '全部',
  [ORDER_STATUS.PENDING_PAYMENT]: '待付款',
  [ORDER_STATUS.PENDING_SHIPMENT]: '待发货',
  [ORDER_STATUS.PENDING_RECEIPT]: '待收货',
  [ORDER_STATUS.PENDING_REVIEW]: '待评价',
  [ORDER_STATUS.SUCCESS]: '已完成',
  [ORDER_STATUS.FAILED]: '已取消',
  [ORDER_STATUS.PARTIAL_SHIPMENT]: '部分发货',
  [ORDER_STATUS.PENDING_AUDIT]: '待审核',
  [ORDER_STATUS.CLOSED]: '已关闭',
}

//后端对应的key与前端对应的key映射
export const ORDER_STATUS_KEY = {
  [ORDER_STATUS.PENDING_PAYMENT]: 'unPay',
  [ORDER_STATUS.PENDING_SHIPMENT]: 'payed',
  [ORDER_STATUS.PENDING_RECEIPT]: 'consignment',
  // PENDING_REVIEW(4) 新接口不再提供统计
  /** 订单列表「待审核」角标：与 getOrderCount.pendingAudit 对齐 */
  [ORDER_STATUS.PENDING_AUDIT]: 'pendingAudit',
  [ORDER_STATUS.SUCCESS]: 'success',
  [ORDER_STATUS.FAILED]: 'cancelled',
  [ORDER_STATUS.CLOSED]: 'closed',
}

/**
 * @description 页面显示位置常量
 */
export const DISPLAY_PAGE = {
  HOME: 101, // 首页
  STORE_HOME: 109, // 商城首页
  SOLUTION_HOME: 110, // 方案首页
  HOT_PRODUCTS_HOME: 112, // 爆款精品首页
  CATEGORY_AGGREGATE: 113, // 分类聚合页
}

/**
 * @description 领取状态枚举
 */
export const COUPON_RECEIVE_STATUS = {
  INVALID: -1, // 无效
  CAN_CLAIM: 1, // 可领取
  CAN_USE: 2, // 可使用
}

/**
 * @description 优惠券状态
 */
export const COUPON_STATUS = {
  INVALID: 0, // 已失效
  VALID: 1, // 有效
  USED: 2, // 已使用
}

/**
 * @description 优惠券过期状态
 */
export const COUPON_OVERDUE_STATUS = {
  OVERDUE: 0, // 已过期
  NOT_OVERDUE: 1, // 未过期
}

/**
 * @description 优惠券卡片按钮类型，claim-立即领取，use-去使用，claimed-已领取，soldOut-已抢光
 */
export const COUPON_BUTTON_TYPES = {
  CLAIM: 'claim',
  USE: 'use',
  CLAIMED: 'claimed',
  SOLD_OUT: 'soldOut',
}

export const COUPON_BUTTON_TYPE_VALUES = Object.values(COUPON_BUTTON_TYPES)

/**
 * @description 多页签数据同步频道名称
 */
export const SYNC_CHANNELS = {
  CART: 'tenda-cart-sync',
}

/**
 * @description 多页签数据同步消息类型
 */
export const SYNC_MESSAGES = {
  REFRESH_CART: 'REFRESH_CART',
}

/**
 * @description 页面唯一标识，用于跨页签同步时排除自身
 */
export const TAB_ID = import.meta.client ? Math.random().toString(36).substring(2, 11) : null

/**
 * @description 用户操作类型常量
 */
export const USER_OPERATION_TYPES = {
  CONTRACTOR_ORDER: 1, // 工程商接单流程
  SOLUTION_GUIDE: 2, // 操作指引方案首页
  STORE_GUIDE: 3, // 操作指引商城
  MY_GUIDE: 4, // 操作指引我的
  TOPOLOGY_GUIDE: 5, // 拓扑图指引
  SOLUTION_DETAIL_GUIDE: 6, // 方案详情清单指引
  PAY_CREDIT_BANNER: 7, // 支付页授信横幅
  CART_CREDIT_BANNER: 8, // 购物车授信横幅
}

/**
 * @description 商城商品列表卡片使用的商品状态常量
 */
export const PRODUCT_STATUS = {
  OFF_SHELF: 0,
  ON_SHELF: 1,
}

/**
 * @description 商品退市标记常量
 */
export const PRODUCT_DELISTED_FLAG = {
  DELISTED: 1,
  NOT_DELISTED: 0,
}
